import { Message, Task, Department } from '../types';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { departmentList } from './departments';

export const calculateMetrics = (messages: Message[], tasks: Task[]) => {
  const now = new Date();
  const lastWeek = subDays(now, 7);
  const recentMessages = messages.filter(msg => new Date(msg.createdAt) > lastWeek);
  const previousMessages = messages.filter(msg => new Date(msg.createdAt) <= lastWeek);

  // Calculate active users (unique users who have acknowledged messages or completed tasks)
  const messageUsers = new Set(messages.flatMap(m => m.acknowledgments));
  const taskUsers = new Set(tasks.flatMap(t => [t.assignedTo, t.assignedBy]));
  const activeUsers = new Set([...messageUsers, ...taskUsers]).size;

  // Calculate response rate (percentage of messages that have been acknowledged)
  const acknowledgedMessages = messages.filter(m => m.acknowledgments.length > 0);
  const responseRate = messages.length > 0
    ? Math.round((acknowledgedMessages.length / messages.length) * 100)
    : 0;

  // Calculate previous response rate for trend
  const previousAcknowledged = previousMessages.filter(m => m.acknowledgments.length > 0);
  const previousResponseRate = previousMessages.length > 0
    ? Math.round((previousAcknowledged.length / previousMessages.length) * 100)
    : 0;

  return {
    totalMessages: messages.length,
    messagesTrend: calculateTrend(recentMessages.length, previousMessages.length),
    activeUsers,
    activeUsersTrend: calculateTrend(activeUsers, activeUsers - 2),
    responseRate,
    responseRateTrend: calculateTrend(responseRate, previousResponseRate)
  };
};

export const calculateDepartmentEngagement = (messages: Message[], tasks: Task[]) => {
  const departmentColors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
  ];

  return departmentList.map((dept, index) => {
    // Get messages and tasks for this department
    const deptMessages = messages.filter(msg => msg.departments.includes(dept));
    const deptTasks = tasks.filter(task => task.department === dept);
    
    // Calculate message engagement
    const totalInteractions = deptMessages.reduce((sum, msg) => 
      sum + msg.acknowledgments.length, 0
    );
    const messageEngagement = deptMessages.length > 0
      ? Math.round((totalInteractions / deptMessages.length) * 100)
      : 0;

    // Calculate task completion rate
    const completedTasks = deptTasks.filter(t => t.status === 'COMPLETED').length;
    const taskCompletion = deptTasks.length > 0
      ? Math.round((completedTasks / deptTasks.length) * 100)
      : 0;

    // Calculate overall engagement (average of message and task engagement)
    const engagement = Math.round((messageEngagement + taskCompletion) / 2);

    // Get recent activity
    const recentMessages = deptMessages.filter(msg => {
      const msgDate = new Date(msg.createdAt);
      const sevenDaysAgo = subDays(new Date(), 7);
      return msgDate >= sevenDaysAgo;
    });

    const recentTasks = deptTasks.filter(task => {
      const taskDate = new Date(task.createdAt);
      const sevenDaysAgo = subDays(new Date(), 7);
      return taskDate >= sevenDaysAgo;
    });

    // Calculate activity trend
    const activityTrend = calculateTrend(
      recentMessages.length + recentTasks.length,
      (deptMessages.length + deptTasks.length) - (recentMessages.length + recentTasks.length)
    );

    return {
      department: dept,
      engagement,
      messageCount: deptMessages.length,
      taskCount: deptTasks.length,
      completionRate: taskCompletion,
      activityTrend,
      color: departmentColors[index % departmentColors.length],
    };
  });
};

export const getMessageTimeline = (messages: Message[], tasks: Task[]) => {
  const days = 7;
  const timeline = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = subDays(new Date(), i);
    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);
    
    const dayMessages = messages.filter(msg => {
      const msgDate = new Date(msg.createdAt);
      return msgDate >= dayStart && msgDate <= dayEnd;
    });

    const dayTasks = tasks.filter(task => {
      const taskDate = new Date(task.createdAt);
      return taskDate >= dayStart && taskDate <= dayEnd;
    });

    timeline.push({
      date: format(date, 'EEE'),
      value: dayMessages.length + dayTasks.length,
    });
  }

  return timeline;
};

export const generateInsights = (messages: Message[], tasks: Task[]) => {
  const acknowledgedMessages = messages.filter(m => m.acknowledgments.length > 0);
  const responseRate = messages.length > 0
    ? Math.round((acknowledgedMessages.length / messages.length) * 100)
    : 0;

  const completedTasks = tasks.filter(t => t.status === 'COMPLETED');
  const taskCompletionRate = tasks.length > 0
    ? Math.round((completedTasks.length / tasks.length) * 100)
    : 0;

  // Find most active department
  const departmentActivity = calculateDepartmentEngagement(messages, tasks);
  const topDepartment = departmentActivity.reduce((prev, curr) => 
    curr.engagement > prev.engagement ? curr : prev
  );

  return {
    peakActivity: `${topDepartment.department} leads with ${topDepartment.engagement}% engagement`,
    responseTime: `${responseRate}% response rate across all communications`,
    topPerformer: `${taskCompletionRate}% task completion rate`
  };
};

const calculateTrend = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};